import { MinionDefinition } from './MinionDefinition';
import { MinionDefinitionValidator } from './MinionDefinition';
import {
  UNDEAD_MINION_ID,
  UNDEAD_ATTACK,
  UNDEAD_HP,
  IMPROVED_ABILITY_ID,
  MASTER_BUFF_ABILITY_ID,
  RUNE_KEEPER_ABILITY_ID,
  BLIGHTCASTER_MINION_ID,
  BLIGHTCASTER_HP,
  BLIGHTCASTER_ATTACK,
  BUFFER_MINION_ID,
  BUFFER_HP,
  BUFFER_ATTACK,
  RUNEKEEPER_MINION_ID,
  RUNEKEEPER_HP,
  RUNEKEEPER_ATTACK,
  SWARMER_MINION_ID,
  SWARMER_HP,
  SWARMER_ATTACK,
  GLASSCANNON_ABILITY_ID,
  EXPLOSIVE_ABILITY_ID,
  TREASURE_CHEST_MINION_ID,
  TREASURE_CHEST_HP,
  TREASURE_CHEST_ATTACK,
  TREASURE_ABILITY_ID,
  LIFE_CHEST_MINION_ID,
  LIFE_CHEST_HP,
  LIFE_CHEST_ATTACK,
  LIFE_FULL_ABILITY_ID,
  EXPLODING_BARREL_MINION_ID,
  EXPLODING_BARREL_HP,
  EXPLODING_BARREL_ATTACK,
  SPELLMAKER_ABILITY_ID,
} from './AbilityConstants';

/**
 * The definitive card pool for Project_X.
 * Exactly 5 cards — one per class — drawn with equal probability.
 *
 * NOTE: The Undead card (UNDEAD_MINION_DEFINITION below) is NOT in this
 * base pool — it can only be drawn while a Gravedigger is on the battlefield
 * (the ability adds it to its owner's draw pool) or via the Rune of the
 * Necromancer (50% of the owner's draws). In combat it can also appear as a
 * temporary copy spawned by the Necromancy ability or the Rune of the Dead.
 *
 * NOTE: The three LOCKED classes (BLIGHTCASTER_MINION_DEFINITION,
 * BUFFER_MINION_DEFINITION, RUNEKEEPER_MINION_DEFINITION below) are ALSO
 * not in this base pool — they have NO acquisition path at all yet (no
 * draw/buy/summon/replacement source), so they can never be drawn or played.
 */
export const testMinions: MinionDefinition[] = [
  {
    id: 'warrior',
    name: 'Warrior',
    startingHp: 5,
    attack: 7,
    minionType: 'warrior',
    baseAbilities: [],
    allowedUpgradePaths: ['path1'],
  },
  {
    id: 'tank',
    name: 'Tank',
    startingHp: 11,
    attack: 1,
    minionType: 'tank',
    baseAbilities: ['shield'],
    allowedUpgradePaths: ['path1'],
  },
  {
    id: 'bard',
    name: 'Bard',
    startingHp: 4,
    attack: 1,
    minionType: 'bard',
    baseAbilities: ['buff_vitality'],
    allowedUpgradePaths: ['path1'],
  },
  {
    id: 'mage',
    name: 'Mage',
    startingHp: 2,
    attack: 10,
    minionType: 'mage',
    baseAbilities: [],
    allowedUpgradePaths: ['path1'],
  },
  {
    id: 'healer',
    name: 'Healer',
    startingHp: 8,
    attack: 1,
    minionType: 'healer',
    baseAbilities: ['heal'],
    allowedUpgradePaths: ['path1'],
  },
];

// Validate all definitions at module load time
testMinions.forEach(def => {
  MinionDefinitionValidator.validate(def);
});

/**
 * The Undead — a 16/12 creature that reaches the board two ways.
 *
 * As a CARD it is deliberately NOT part of the base draw pool
 * (`testMinions`): it can only be drawn while a Gravedigger is on the
 * battlefield (the ability adds it to its owner's draw pool) or via the
 * Rune of the Necromancer (50% of the owner's draws). Once drawn it is a
 * real card — played, sold and upgraded like any other.
 *
 * In COMBAT it can also appear as a temporary copy: the Necromancy ability
 * (or the Rune of the Dead) replaces a dead friendly minion with a living
 * Undead combat copy for the duration of the combat. When combat ends the
 * copy is removed and the original minion is restored from the pre-combat
 * snapshot.
 *
 * Every Undead starts with the Improved ability: +1/+1 for every previous
 * Undead that has been played from a hand or summoned in combat this game.
 * The bonus is derived from the match-wide counter (see IMPROVED_ABILITY_ID),
 * so a freshly spawned Undead may fight with stats above 16/12.
 */
export const UNDEAD_MINION_DEFINITION: MinionDefinition = MinionDefinitionValidator.validate({
  id: UNDEAD_MINION_ID,
  name: 'Undead',
  startingHp: UNDEAD_HP,
  attack: UNDEAD_ATTACK,
  minionType: 'undead',
  baseAbilities: [IMPROVED_ABILITY_ID],
  allowedUpgradePaths: [],
});

/**
 * LOCKED CLASSES (not yet obtainable — see the AbilityConstants header).
 * Exported but deliberately NOT part of the `testMinions` base draw pool
 * and with NO acquisition source: for now there is no way to get or play
 * any of these classes.
 */

/**
 * The Blightcaster — a 16/1 blight-caster (slime-green class color).
 * No base abilities.
 */
export const BLIGHTCASTER_MINION_DEFINITION: MinionDefinition = MinionDefinitionValidator.validate({
  id: BLIGHTCASTER_MINION_ID,
  name: 'Blightcaster',
  startingHp: BLIGHTCASTER_HP,
  attack: BLIGHTCASTER_ATTACK,
  minionType: 'blightcaster',
  baseAbilities: [],
  allowedUpgradePaths: [],
});

/**
 * The Buffer — a 15/2 buff specialist (yellow class color) that STARTS with
 * the Master Buff ability (the Bard-only acquisition rule never applies to
 * it — it has the ability from level 1 and, via the two-direction rule,
 * can never be offered the two replaced buffs).
 */
export const BUFFER_MINION_DEFINITION: MinionDefinition = MinionDefinitionValidator.validate({
  id: BUFFER_MINION_ID,
  name: 'Buffer',
  startingHp: BUFFER_HP,
  attack: BUFFER_ATTACK,
  minionType: 'buffer',
  baseAbilities: [MASTER_BUFF_ABILITY_ID],
  allowedUpgradePaths: [],
});

/**
 * The Runekeeper — a 10/1 rune specialist (tile blue-gray class color) that
 * STARTS with the RuneKeeper ability (+1 rune purchase cap per game while
 * in play). Can additionally learn Shield (level 1) and RuneFeast (level
 * 3); like every class it can also learn Grandmaster (level 5) and Second
 * Chance (level 6, Grandmaster rule).
 */
export const RUNEKEEPER_MINION_DEFINITION: MinionDefinition = MinionDefinitionValidator.validate({
  id: RUNEKEEPER_MINION_ID,
  name: 'Runekeeper',
  startingHp: RUNEKEEPER_HP,
  attack: RUNEKEEPER_ATTACK,
  minionType: 'runekeeper',
  baseAbilities: [RUNE_KEEPER_ABILITY_ID],
  allowedUpgradePaths: [],
});

/**
 * The Swarmer — a 1/10 glass-cannon (brown class color) that STARTS with
 * the Glasscannon ability (this minion dies after it has attacked).
 *
 * LOCKED CLASS: NOT part of the `testMinions` base draw pool and with NO
 * draw/buy/summon/replacement source — it can never be drawn or played.
 * It reaches the board ONLY as a TEMPORARY COMBAT TOKEN spawned by the
 * SwarmLord ability (Mage/Blightcaster, level 5 — see AbilityConstants):
 * when a SwarmLord holder dies in combat, the owner's board is filled with
 * fresh level-1 Swarmers that each attack exactly once (and die of their
 * Glasscannon) before the opposing side's next turn. The tokens exist
 * only in the combat copies (the Necromancy-Undead convention) and are
 * discarded when combat ends — battlefield restoration brings back the
 * original minions.
 */
export const SWARMER_MINION_DEFINITION: MinionDefinition = MinionDefinitionValidator.validate({
  id: SWARMER_MINION_ID,
  name: 'Swarmer',
  startingHp: SWARMER_HP,
  attack: SWARMER_ATTACK,
  minionType: 'swarmer',
  baseAbilities: [GLASSCANNON_ABILITY_ID],
  allowedUpgradePaths: [],
});

// TASK 204: the accidental Spawner class (a Swarmer misspelling) and its
// minion definition were REMOVED. The Infestation token is the Swarmer
// itself (SWARMER_MINION_DEFINITION, a Fragile Swarmer — see
// RoundCoordinator.applyInfestation).

/**
 * ITEMS (Task: Items) — the `item` class. Items are class cards that CAN
 * sit on the battlefield (a future acquisition path will place them) but
 * are deliberately NOT part of the `testMinions` base draw pool and have
 * NO acquisition source yet — like the locked classes, there is no way to
 * draw, buy, summon or replace into one today.
 *
 * Class rules (see the AbilityConstants "Item class" header): they NEVER
 * attack (the RoundCoordinator always skips an item's turn), they CAN deal
 * counter damage like regular minions, they are always level 1 and can
 * never be upgraded, they CAN carry keywords, they render light yellow
 * with a PER-CARD image (the user cannot change an item's skin), and they
 * appear in no skin menu (no SkinDefinition entries).
 */

/**
 * The Treasure Chest — a 25/0 item (light yellow class color) that STARTS
 * with the Treasure ability: the player who kills it gains 25 gold (the
 * centralized death-gold rule — RoundCoordinator._awardDeathGold).
 */
export const TREASURE_CHEST_MINION_DEFINITION: MinionDefinition = MinionDefinitionValidator.validate({
  id: TREASURE_CHEST_MINION_ID,
  name: 'Treasure chest',
  startingHp: TREASURE_CHEST_HP,
  attack: TREASURE_CHEST_ATTACK,
  minionType: 'item',
  baseAbilities: [TREASURE_ABILITY_ID],
  allowedUpgradePaths: [],
});

/**
 * The Life Chest — a 25/5 item (light yellow class color) that STARTS with
 * the Life-full ability: the player who kills it instantly gains 1 life.
 * Its 5 attack deals COUNTER damage only (items never attack — a 5-HP
 * attacker that hits it dies to the counter-attack).
 */
export const LIFE_CHEST_MINION_DEFINITION: MinionDefinition = MinionDefinitionValidator.validate({
  id: LIFE_CHEST_MINION_ID,
  name: 'Life chest',
  startingHp: LIFE_CHEST_HP,
  attack: LIFE_CHEST_ATTACK,
  minionType: 'item',
  baseAbilities: [LIFE_FULL_ABILITY_ID],
  allowedUpgradePaths: [],
});

/**
 * The Exploding Barrel — a 12/0 item (light yellow class color) that STARTS
 * with the Explosive ability: when it DIES in combat (any death source)
 * its OnDeath window detonates — the existing Explosive pipeline (the
 * blast hits every living minion on both sides, kill gold / credit / FX
 * all follow the established conventions; the barrel's own side gets no
 * friendly-fire gold/credit for the deaths its blast causes on itself).
 */
export const EXPLODING_BARREL_MINION_DEFINITION: MinionDefinition = MinionDefinitionValidator.validate({
  id: EXPLODING_BARREL_MINION_ID,
  name: 'Exploding Barrel',
  startingHp: EXPLODING_BARREL_HP,
  attack: EXPLODING_BARREL_ATTACK,
  minionType: 'item',
  baseAbilities: [EXPLOSIVE_ABILITY_ID],
  allowedUpgradePaths: [],
});

/**
 * SPELLS (Task: Spells) — the `spell` class. Spells are instant cards: they
 * occupy a hand slot, sell for their own fixed gold value (SpellDefinitions
 * `sellValue`), carry a LEVEL (rendered centered at the bottom of the card),
 * can be given to a COOP teammate like a minion, and are PLAYED by casting
 * (the effect goes into play and the card despawns — they NEVER land on the
 * battlefield).
 *
 * NOT part of the `testMinions` BASE draw pool — they are drawn ONLY through
 * the Rune of the Mage (RuneDefinitions): while the owner (or, in COOP, the
 * owner's teammate) has the rune, every draw has a chance — equal to any
 * single class card — to be a random level-1 spell.
 */
export { POWER_TOUCH_MINION_DEFINITION, HEALING_RAIN_MINION_DEFINITION } from './SpellDefinitions';

/**
 * SPELLMAKER (Task: Spellmaker class) — the `spellmaker` class: a 10/1
 * baby-blue specialist that STARTS with the Spellmaker keyword (adds spells
 * to the owner's draw pile — see AbilityConstants) and can learn
 * SpellMaster at level 5 (it REPLACES Spellmaker and counts twice).
 *
 * LOCKED: NOT part of the `testMinions` base draw pool — it can never be
 * drawn. It reaches the board via the Healer TRANSFORM (the `to_spellmaker`
 * spec in TransformDefinitions, level 3).
 */
export const SPELLMAKER_MINION_DEFINITION: MinionDefinition = MinionDefinitionValidator.validate({
  id: 'spellmaker',
  name: 'Spellmaker',
  startingHp: 10,
  attack: 1,
  minionType: 'spellmaker',
  baseAbilities: [SPELLMAKER_ABILITY_ID],
  allowedUpgradePaths: [],
});

/**
 * The BASE (level-1) definition for a minion class: the draw-pool entry for
 * the drawable classes, the exported locked-card definition otherwise. One
 * map covers every class the Transformed spell can roll (all classes minus
 * swarmer/spell/item — see TRANSFORMED_SPELL_TARGET_CLASSES).
 */
const BASE_DEFINITION_BY_CLASS: Map<string, MinionDefinition> = (() => {
  const byClass = new Map<string, MinionDefinition>();
  for (const def of testMinions) byClass.set(def.minionType, def);
  for (const def of [
    UNDEAD_MINION_DEFINITION,
    BLIGHTCASTER_MINION_DEFINITION,
    BUFFER_MINION_DEFINITION,
    RUNEKEEPER_MINION_DEFINITION,
    SPELLMAKER_MINION_DEFINITION,
  ]) {
    byClass.set(def.minionType, def);
  }
  return byClass;
})();

/**
 * The base (level-1) definition of a minion class, or null for classes
 * without one (swarmer/spell/item — the Transformed spell never rolls
 * them). Used by the Transformed spell: the transformed minion is reset to
 * a LEVEL-1 minion of the new class (base stats + base abilities).
 */
export function getBaseDefinitionForClass(minionType: string): MinionDefinition | null {
  return BASE_DEFINITION_BY_CLASS.get(minionType) ?? null;
}
